import React from 'react';
import { useOptionalFilterState } from './useFilter';
import {
  BilderitModuleId,
  RoleNames,
  useGetAllCompaniesForProjectOptionsQuery,
  useGetRolesWithNestedProjectsQuery,
} from '@/generated/codegen';
import { useAuthState } from '@/utils/auth';
import { isNotNullish } from '@/utils/nullish';

/**
 * Hook to check if a feature/module is enabled for the current company.
 *
 * Features are enabled at the company level and can have an optional expiration date.
 *
 * Optionally, Superroot user always has access to all features.
 *
 * @param superrootAlwaysEnabled - If true, Superroot users will always have all features enabled (default: false)
 * @param overrideCompanyId - Optional company ID to use instead of the one from filter context (useful for maquinista app where there's no filter context)
 *
 * @example
 * ```tsx
 * const { isFeatureEnabled, loading } = useFeatureFlags();
 *
 * if (isFeatureEnabled(BilderitModuleId.WeeklyReports)) {
 *   return <WeeklyReportsView />;
 * }
 *
 * return <FeatureDisabledMessage />;
 * ```
 */
export const useFeatureFlags = (
  superrootAlwaysEnabled = false,
  overrideCompanyId?: number | null
) => {
  const filterCompanyId = useOptionalFilterState().value.company_id;
  const companyId = overrideCompanyId ?? filterCompanyId;
  const user = useAuthState().user;
  const isSuperRoot = user?.roles
    .filter(isNotNullish)
    .some((r) => r.role === RoleNames.SuperRoot);

  const { data: projectsData, loading } = useGetRolesWithNestedProjectsQuery({
    skip: !user,
    fetchPolicy: 'cache-first',
  });
  // This is for a superRoot user... in that case the company info does not come in the roles
  // array, so we have to get it ourselves
  const { data: allCompaniesDataForSuperRoot } =
    useGetAllCompaniesForProjectOptionsQuery({
      skip: !isSuperRoot,
    });

  const modulesEnabled = React.useMemo(
    () =>
      (isSuperRoot
        ? allCompaniesDataForSuperRoot?.getAllCompanies.items?.find(
            (c) => c?.id === companyId
          )?.modules_enabled
        : projectsData?.getMe?.__typename === 'GetMe'
          ? projectsData.getMe.item.roles.find(
              (r) => r?.company?.id === companyId
            )?.company?.modules_enabled
          : []) ?? [],
    [
      isSuperRoot,
      allCompaniesDataForSuperRoot?.getAllCompanies.items,
      projectsData?.getMe,
      companyId,
    ]
  );

  /**
   * Check if a specific feature is enabled for the current company.
   * A feature is considered enabled if:
   * 1. It exists in the company's modules_enabled list
   * 2. It hasn't expired (end_date is null or in the future)
   *
   * Optionally, Superroot user always has all features enabled.
   *
   * @param feature - The feature ID to check
   * @returns true if the feature is enabled, false otherwise
   */
  const isFeatureEnabled = React.useCallback(
    (feature: BilderitModuleId): boolean => {
      // Superroot user has all features enabled
      if (superrootAlwaysEnabled && isSuperRoot) {
        return true;
      }

      const now = new Date();
      const featureFlag = modulesEnabled.find((m) => m?.module_id === feature);

      if (!featureFlag) {
        return false;
      }

      // If there's an end_date, check if it hasn't expired
      if (featureFlag.end_date) {
        const endDate = new Date(featureFlag.end_date);
        if (endDate <= now) {
          return false;
        }
      }

      return true;
    },
    [isSuperRoot, modulesEnabled, superrootAlwaysEnabled]
  );

  /**
   * Get all enabled features for the current company.
   * Only returns features that haven't expired.
   *
   * Optionally, Superroot user always has all features enabled.
   *
   * @returns Array of enabled feature IDs
   */
  const getEnabledFeatures = React.useCallback((): BilderitModuleId[] => {
    // Superroot user has all features enabled
    if (superrootAlwaysEnabled && isSuperRoot) {
      return Object.values(BilderitModuleId);
    }

    if (!modulesEnabled) {
      return [];
    }

    const now = new Date();
    return modulesEnabled
      .filter(isNotNullish)
      .filter((m) =>
        // Check expiration
        m.end_date ? new Date(m.end_date) > now : true
      )
      .map((m) => m!.module_id);
  }, [isSuperRoot, modulesEnabled, superrootAlwaysEnabled]);

  return {
    isFeatureEnabled,
    getEnabledFeatures,
    loading,
    companyId,
  };
};
